/**
 * triples-to-bundle.ts — Phase 438 Plan 01 minimum-viable reconstructor.
 *
 * Internal helper: walks `TripleJson[]` (Phase 0436 application_ontology row
 * shape) and reconstructs an in-memory `BlockBundle` so the existing pure
 * stage runners (runActionFlowStage, runStructuralStage, runRuntimeExprStage)
 * can validate Layer 2 jsonb input without round-tripping through the
 * filesystem.
 *
 * STRATEGY (Plan 01):
 *
 *   Plan 01 ships a *minimum-viable* reconstructor with TWO paths:
 *
 *   1. **`cogo:bundleAs` short-circuit (preferred for Plan 01).** If the
 *      instance triples include a meta-triple
 *
 *        ?subject cogo:bundleAs "<json-string>"
 *
 *      where the literal value is a complete pre-serialized `BlockBundle`
 *      JSON (per Phase 0436 D-D3 inline JSON literal pattern), we parse the
 *      literal directly and return the bundle. This sidesteps full
 *      ontology→bundle materialization (which is a future optimization
 *      track) and lets Plan 01 ship while honoring the public API contract
 *      (`validateActionFlow({vocabulary, instance})` accepts TripleJson[]).
 *
 *   2. **Empty fallback.** If no `cogo:bundleAs` triple is found, return an
 *      empty BlockBundle (all six slots = `{}`). Stage runners are tolerant
 *      of empty input — `runActionFlowStage` returns `passed:true` with zero
 *      issues when `bundle.flow.flows` is missing or non-object.
 *
 * Future phases (Plan 02 / Plan 03 or a successor phase) MAY introduce a
 * third path that walks the predicates of the cogo: ontology
 * (cogo:hasFlow / cogo:hasUi / cogo:hasEvent / etc.) and materializes the
 * bundle slot-by-slot from individual triples. That work is out of scope
 * for Plan 01 — the public API contract is the function signature, NOT
 * the internal reconstruction strategy, so the engine can evolve.
 *
 * RULES (D-A2, D-D1, D-D2):
 *
 *   - Zero filesystem I/O (no node:fs / node:path / node:url / node:module).
 *   - Phase 0436 D-D1: object_value is for IRIs/bnodes (which we recover
 *     via `o_type === "iri" | "bnode"`); object_jsonb is for ALL literals.
 *     `cogo:bundleAs` literal values arrive as `o_type: "literal"` with the
 *     bundle JSON serialized into `t.o`.
 *   - Phase 0436 D-D2: subject/predicate/IRI-form object are fully-expanded
 *     IRIs. The `cogo:` namespace is the canonical Cogo platform schema IRI;
 *     this module accepts both `cogo:bundleAs` (short form, used in spec
 *     prose) and the fully-expanded form via the `BUNDLE_AS_IRIS` set.
 */
import type { TripleJson } from "./codec"
import type { BlockBundle } from "../types"

/**
 * The set of accepted predicate IRIs that flag an inline-JSON BlockBundle.
 * In production triples emitted by Phase 0436's ETL the predicate will be
 * the fully-expanded form. The short forms ("cogo:bundleAs", "bundleAs")
 * are accepted for tests + dev fixtures convenience.
 */
const BUNDLE_AS_IRIS: ReadonlySet<string> = new Set([
  "https://cogo.xyz/ontology/v1#bundleAs",
  "https://cogo.xyz/ontology/cogo#bundleAs",
  "http://cogo.xyz/ontology#bundleAs",
  "cogo:bundleAs",
  "bundleAs",
])

/** Empty BlockBundle scaffold — every slot is an empty record. */
function emptyBundle(): BlockBundle {
  return {
    block: {},
    ui: {},
    events: {},
    flow: {},
    variables: {},
    datasource: {},
  }
}

/**
 * Find the first `cogo:bundleAs` triple's literal payload (if any), parse it
 * as JSON, and merge it onto an empty BlockBundle scaffold so all six slots
 * are present (stage runners assume the slots exist).
 */
function tryBundleAs(triples: ReadonlyArray<TripleJson>): BlockBundle | null {
  for (const t of triples) {
    if (!BUNDLE_AS_IRIS.has(t.p)) continue
    if (t.o_type !== "literal") continue
    let parsed: unknown
    try {
      parsed = JSON.parse(t.o)
    } catch {
      // Malformed JSON literal — skip and let later triples (or empty
      // fallback) handle. Plan 01 does not surface a parse error here;
      // stage runners will simply observe an empty bundle.
      continue
    }
    if (!parsed || typeof parsed !== "object") continue

    const partial = parsed as Partial<BlockBundle>
    const out = emptyBundle()
    if (partial.block && typeof partial.block === "object") out.block = partial.block as Record<string, unknown>
    if (partial.ui && typeof partial.ui === "object") out.ui = partial.ui as Record<string, unknown>
    if (partial.events && typeof partial.events === "object") out.events = partial.events as Record<string, unknown>
    if (partial.flow && typeof partial.flow === "object") out.flow = partial.flow as Record<string, unknown>
    if (partial.variables && typeof partial.variables === "object") out.variables = partial.variables as Record<string, unknown>
    if (partial.datasource && typeof partial.datasource === "object") out.datasource = partial.datasource as Record<string, unknown>
    return out
  }
  return null
}

/**
 * Reconstruct a `BlockBundle` from a TripleJson[] instance graph.
 *
 * Plan 01 minimum-viable strategy:
 *   1. Look for a `cogo:bundleAs` literal triple → JSON.parse → return.
 *   2. Otherwise return an empty BlockBundle (stage runners handle this
 *      gracefully — see runActionFlowStage early-return at L53-62).
 */
export function triplesToBlockBundle(
  triples: ReadonlyArray<TripleJson>,
): BlockBundle {
  if (triples.length === 0) return emptyBundle()
  const fromBundleAs = tryBundleAs(triples)
  if (fromBundleAs) return fromBundleAs
  return emptyBundle()
}
